import React, { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  Filter,
  Building,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { facilitiesApi } from "../../utils/api";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const SEVERITY_STYLES = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

const TelegramViolations = () => {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [violations, setViolations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reviewedFilter, setReviewedFilter] = useState("false");
  const [expanded, setExpanded] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);

  useEffect(() => {
    facilitiesApi.getAll().then((data) => setFacilities(data || []));
  }, []);

  const fetchViolations = useCallback(() => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      facility_id: facilityId,
      limit: "100",
    });
    if (reviewedFilter !== "all") params.set("reviewed", reviewedFilter);
    fetch(`${AUTH_URL}/tg-violations?${params}`, { headers })
      .then((r) => r.json())
      .then((data) => {
        setViolations(data.violations || []);
        setTotal(data.total || 0);
      })
      .catch(() => setError("Failed to load violations"))
      .finally(() => setLoading(false));
  }, [facilityId, reviewedFilter]);

  useEffect(() => {
    fetchViolations();
  }, [fetchViolations]);

  const handleReview = async (id) => {
    setReviewingId(id);
    try {
      const res = await fetch(`${AUTH_URL}/tg-violations/${id}/review`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setViolations((prev) =>
          reviewedFilter === "false"
            ? prev.filter((v) => v.id !== id)
            : prev.map((v) => (v.id === id ? { ...v, reviewed: true } : v)),
        );
      }
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          <h2 className="text-xl font-bold text-slate-800">
            Moderation Violations
          </h2>
        </div>
        <button
          onClick={fetchViolations}
          disabled={loading || !facilityId}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Building className="w-4 h-4 text-slate-400" />
          <select
            value={facilityId}
            onChange={(e) => {
              setFacilityId(e.target.value);
              setViolations([]);
            }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">— select facility —</option>
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.display_name || f.facility_name || f.facility_id}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={reviewedFilter}
            onChange={(e) => setReviewedFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="false">Pending review</option>
            <option value="true">Reviewed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {facilityId && !loading && violations.length === 0 && (
        <div className="flex flex-col items-center py-16 text-slate-400">
          <CheckCircle className="w-10 h-10 mb-2" />
          <p className="text-sm">No violations to review</p>
        </div>
      )}

      {violations.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {total} violation{total !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="divide-y divide-slate-100">
            {violations.map((v) => (
              <div key={v.id} className="px-4 py-3">
                <div
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                >
                  {/* Severity badge */}
                  <span
                    className={`mt-0.5 px-2 py-0.5 rounded text-xs font-semibold shrink-0 ${
                      SEVERITY_STYLES[v.severity] || SEVERITY_STYLES.medium
                    }`}
                  >
                    {v.severity}
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">
                        {v.violation_type}
                      </span>
                      {v.username && (
                        <span className="text-xs text-slate-500">
                          @{v.username}
                        </span>
                      )}
                      {v.reviewed && (
                        <span className="text-xs text-green-600 font-medium">
                          ✓ reviewed
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {v.message_text || "— no message —"}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Group {v.group_id} ·{" "}
                      {new Date(v.created_at).toLocaleString()}
                      {v.action_taken && ` · ${v.action_taken}`}
                      {v.ai_confidence != null &&
                        ` · ${(v.ai_confidence * 100).toFixed(0)}% confidence`}
                    </p>
                  </div>

                  <div className="shrink-0 flex items-center gap-2">
                    {!v.reviewed && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReview(v.id);
                        }}
                        disabled={reviewingId === v.id}
                        className="px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                      >
                        {reviewingId === v.id ? "…" : "Mark Reviewed"}
                      </button>
                    )}
                    {expanded === v.id ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>

                {expanded === v.id && (
                  <div className="mt-3 ml-16 p-3 bg-slate-50 rounded-lg text-sm space-y-1.5">
                    {v.message_text && (
                      <p className="text-slate-700 whitespace-pre-wrap">
                        {v.message_text}
                      </p>
                    )}
                    {v.notes && (
                      <p className="text-slate-500 italic text-xs">
                        Notes: {v.notes}
                      </p>
                    )}
                    {v.reviewed_by && (
                      <p className="text-xs text-slate-400">
                        Reviewed by {v.reviewed_by} on{" "}
                        {new Date(v.reviewed_at).toLocaleString()}
                      </p>
                    )}
                    <div className="flex gap-4 text-xs text-slate-400 pt-1">
                      {v.user_id && <span>User ID: {v.user_id}</span>}
                      {v.message_id && <span>Msg ID: {v.message_id}</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramViolations;
